import React, { useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import moment from "moment-timezone"
import { Flex, NotificationHub } from "@nef/core"
import { useHistory } from "react-router-dom"

import { AppDispatch } from "../../app/store"
import { fetchInstrumentsPendingApprovalCount, fetchIsDLPosted } from "../../redux/dailyList/actions"
import { getServerTimeFormatForDisplay } from "../../utils/datetimehandler"
import useGetUserAndRole from "../../hooks/useGetUserAndRole"
import { USER_ROLES } from "../../types/_enums"

const SystemStatus = () => {
  const [isDLPosted, setIsDLPosted] = useState<boolean>(false)
  const [isRegistrationOpen, setIsRegistrationOpen] = useState<boolean>(true)
  const [serverTime, setServerTime] = useState<string>("")
  const [needApprovalCount, setNeedApprovalCount] = useState<number>(0)
  const dispatch = useDispatch<AppDispatch>()
  const history = useHistory()
  const [userRole]: any[] = useGetUserAndRole()

  // API to get daily list status
  const getDLStatus = async () => {
    try {
      const { data, headers } = await dispatch(fetchIsDLPosted()).unwrap()
      setIsDLPosted(data)
    } catch (error: any) {
      NotificationHub.send("danger", `${error.status} ${error.message}`)
    }
  }

  //* api to check for the DL Exceptions count (instruments pending approval with effective date as NBD)
  // ! ONLY CALL THIS API FOR SUPERVISOR
  const getInstrumentsPendingAprrovalCount = async () => {
    if (!userRole.includes(USER_ROLES.SUPERVISOR)) return
    try {
      const res = await dispatch(fetchInstrumentsPendingApprovalCount()).unwrap()
      setNeedApprovalCount(res)
    } catch (error: any) {
      NotificationHub.send("danger", `${error.status} ${error.message}`)
    }
  }

  //* prevent deafult reloading of the page for anchor tag
  const handleNeedApprovalClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    history.push("/user_dashboard")
  }

  // check if the registration is closed on weekends
  const checkIfRegistrationClosed = useCallback(() => {
    setIsRegistrationOpen(moment(serverTime).day() % 6 !== 0)
  }, [])

  // get daily list posted status every minute
  useEffect(() => {
    let interval: any
    if (!isDLPosted) {
      interval = setInterval(() => {
        getDLStatus()
      }, 60000)
    }
    // cleanup
    return () => clearInterval(interval)
  }, [isDLPosted])

  useEffect(() => {
    if (!userRole.length) return
    getInstrumentsPendingAprrovalCount()
  }, [userRole])

  useEffect(() => {
    // get daily list status on component mount
    getDLStatus()
    // set initial server time and check registration status
    const interval = setInterval(() => {
      const etTime = getServerTimeFormatForDisplay(moment())
      setServerTime(etTime) // Update the server time state
      checkIfRegistrationClosed() // Check if registration is closed based on the updated server time
    }, 1000)
    // Cleanup interval on component unmount
    return () => clearInterval(interval)
  }, [])

  return (
    <>
      <div className="sys-status__container">
        {/* server time in ET */} 
        <div className="sys-status__item">
          <span className="sys-status__time">
            <span>Server Time</span>
            {serverTime}
          </span>
        </div>
        <Flex flexDirection="column" alignItems="start" gap=".5rem">
          {needApprovalCount ? (
            <a href="/user_dashboard" onClick={handleNeedApprovalClick} className="sys-status__item sys-status__item--important">
              Need Approval :<span className="">{needApprovalCount}</span>
            </a>
          ) : null}
          <Flex gap="1rem">
            <div className="sys-status__item">
              Registration Site :
              <span className={`sys-status__pill sys-status__pill--${isRegistrationOpen ? "primary" : "secondary"}`}>
                {isRegistrationOpen ? "OPEN" : "CLOSED"}
              </span>
            </div>
            <div className="sys-status__item">
              Daily List :
              <span className={`sys-status__pill sys-status__pill--${isDLPosted ? "primary" : "secondary"}`}>
                {isDLPosted ? "POSTED" : "PENDING"}
              </span>
            </div>
          </Flex>
        </Flex>
      </div>
    </>
  )
}

export default SystemStatus
